import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { cn } from '../lib/utils';
import { toast } from 'sonner';
import {
  CheckCircle2, AlertTriangle, Loader2, RotateCcw, ExternalLink, ArrowRight,
} from 'lucide-react';
import { TaskPreviewTable } from './TaskPreviewTable';
import { EntityPreviewCard } from './EntityPreviewCard';

const API = '/api';

const STATUS_DOT = {
  completed: 'bg-emerald-500',
  failed: 'bg-red-500',
  pending_confirmation: 'bg-amber-500 animate-pulse',
  running: 'bg-sky-500 animate-pulse',
};

function useUndoCountdown(deadlineIso) {
  const [secs, setSecs] = useState(() => {
    if (!deadlineIso) return null;
    return Math.max(0, Math.floor((new Date(deadlineIso).getTime() - Date.now()) / 1000));
  });
  useEffect(() => {
    if (!deadlineIso) return;
    const tick = () => {
      const remaining = Math.max(0, Math.floor((new Date(deadlineIso).getTime() - Date.now()) / 1000));
      setSecs(remaining);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [deadlineIso]);
  return secs;
}

const InlineUndoChip = ({ actionRunId, deadlineIso, onUndone }) => {
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const secs = useUndoCountdown(deadlineIso);
  if (done) return (
    <span className="inline-flex items-center gap-1 text-[10px] text-emerald-700 dark:text-emerald-400">
      <CheckCircle2 className="w-3 h-3" /> undone
    </span>
  );
  if (!deadlineIso || secs == null || secs <= 0) return null;
  return (
    <button
      type="button"
      disabled={busy}
      onClick={async () => {
        setBusy(true);
        try {
          const r = await fetch(`${API}/ai/v2/actions/${actionRunId}/undo`, {
            method: 'POST', credentials: 'include',
            headers: {'Content-Type':'application/json'}, body: '{}'
          });
          const d = await r.json();
          if (r.ok && d.success) {
            setDone(true);
            toast.success('Undone', { description: d.message });
            onUndone?.(actionRunId);
          } else {
            toast.error(d.error_code || 'AI-UNDO', { description: d.message || 'Undo failed' });
          }
        } finally { setBusy(false); }
      }}
      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full border border-amber-300 bg-amber-50 text-amber-700 hover:bg-amber-100 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-300 text-[10px] transition"
      data-testid={`chat-undo-${actionRunId}`}
    >
      {busy ? <Loader2 className="w-2.5 h-2.5 animate-spin" /> : <RotateCcw className="w-2.5 h-2.5" />}
      Undo · {secs}s
    </button>
  );
};

const ScratchpadStepper = ({ steps, onUndone }) => {
  if (!steps || steps.length === 0) return null;
  return (
    <div className="mt-2 space-y-1" data-testid="chat-scratchpad">
      {steps.map((s, i) => (
        <div key={`scratch-${i}`} className="flex items-center gap-2 text-[10px]" data-testid={`chat-scratch-step-${i}`}>
          <span className={cn('w-1.5 h-1.5 rounded-full flex-shrink-0', STATUS_DOT[s.status] || 'bg-muted')} />
          <span className="font-mono text-muted-foreground">{s.capability_id}</span>
          <ArrowRight className="w-2 h-2 text-muted-foreground/50" />
          <span className="text-muted-foreground truncate flex-1">{s.summary}</span>
          {s.action_run_id && s.undo_deadline && (
            <InlineUndoChip
              actionRunId={s.action_run_id}
              deadlineIso={s.undo_deadline}
              onUndone={onUndone}
            />
          )}
        </div>
      ))}
    </div>
  );
};

const TASK_EDITOR_CAPS = new Set([
  'orka.tasks.bulk_create',
  'orka.tasks.create',
  'orka.tasks.update',
]);

const PendingActionCard = ({ pending, onConfirmed }) => {
  const [done, setDone] = useState(false);
  if (done) return null;

  // Route ALL task-creating / task-updating capabilities through the rich
  // editable table preview. Everything else gets the generic entity preview
  // (clean key/value grid with smart field controls — no raw JSON).
  if (TASK_EDITOR_CAPS.has(pending.capability_id)) {
    return (
      <TaskPreviewTable
        pending={pending}
        onConfirmed={(paId, d) => { setDone(true); onConfirmed?.(paId, d); }}
      />
    );
  }
  return (
    <EntityPreviewCard
      pending={pending}
      onConfirmed={(paId, d) => { setDone(true); onConfirmed?.(paId, d); }}
    />
  );
};

const ToolFailureChip = ({ failure, idx }) => (
  <div
    className="mt-2 flex items-start gap-1.5 rounded-lg border border-red-300 bg-red-50 dark:border-red-900 dark:bg-red-950/40 px-2 py-1.5"
    data-testid={`chat-tool-failure-${idx}`}
  >
    <AlertTriangle className="w-3 h-3 mt-0.5 flex-shrink-0 text-red-600 dark:text-red-400" />
    <div className="flex-1 min-w-0">
      <p className="text-[10px] font-mono uppercase tracking-wide text-red-700 dark:text-red-400">
        {failure.error_code || 'AI-TOOL'} · {failure.capability_name || failure.capability_id}
      </p>
      <p className="text-[11px] text-red-800 dark:text-red-200 truncate">
        {failure.error || 'Action failed'}
      </p>
    </div>
    {failure.deck_item_id && (
      <Link
        to="/ai"
        className="flex items-center gap-0.5 text-[10px] font-medium text-red-700 dark:text-red-300 hover:underline whitespace-nowrap"
        data-testid={`chat-tool-failure-deck-${idx}`}
      >
        Deck <ExternalLink className="w-2.5 h-2.5" />
      </Link>
    )}
  </div>
);

/**
 * Renders all rich blocks attached to an assistant message:
 *   - tool failures
 *   - pending action cards (L2/L3/L4 confirmations)
 *   - scratchpad stepper with inline undo
 *
 * Pass-through onConfirmed/onUndone callbacks let the parent refetch
 * conversation state if needed.
 */
export const ChatBlocks = ({ message, onConfirmed, onUndone }) => {
  const failures = message.tool_failures || [];
  const pendings = message.pending_actions || [];
  const steps = message.scratchpad || [];
  if (failures.length === 0 && pendings.length === 0 && steps.length === 0) return null;
  return (
    <>
      {failures.map((f, i) => (
        <ToolFailureChip key={`f-${i}`} failure={f} idx={i} />
      ))}
      {pendings.map((pa) => (
        <PendingActionCard key={pa.pending_action_id} pending={pa} onConfirmed={onConfirmed} />
      ))}
      <ScratchpadStepper steps={steps} onUndone={onUndone} />
    </>
  );
};

export default ChatBlocks;
